/**
 * Client-side filter/sort helpers for Obchody + Organizace directory lists.
 *
 * `dateAsc` / `dateDesc` / `amountAsc` / `amountDesc` exist so directory filter
 * UI (`DirectoryFilterFacets`) can render order-list sorts. Shop helpers
 * (`filterDirectoryShops` / `filterDirectoryOrgs`) ignore them — use
 * `filterDirectoryOrders` from `features/account/ordersDirectoryFilter.ts`.
 */

import { groupDirectoryShopsByDistance } from './directoryDistance.js';

export type DirectoryShopSortId =
  | 'nameAsc'
  | 'nameDesc'
  | 'orgAsc'
  | 'distanceAsc'
  | 'favoriteDesc';

/** Order-list sorts. Assignable to `DirectorySortId` for shared filter chrome. */
export type OrdersDirectorySortId =
  | 'nameAsc'
  | 'nameDesc'
  | 'orgAsc'
  | 'dateAsc'
  | 'dateDesc'
  | 'amountAsc'
  | 'amountDesc';

export type DirectorySortId = DirectoryShopSortId | OrdersDirectorySortId;

/** Wire `sort` for `/public/customer-sales-points` — omit when undefined (server name default). */
export type PublicDirectoryApiSort = 'favoriteCountDesc';

/**
 * FE → API sort matrix for Obchody.
 * `favoriteDesc` → `favoriteCountDesc`; other UI sorts omit (never leave favoriteCountDesc on the wire).
 */
export function mapDirectorySortToApiSort(
  sort: DirectorySortId,
): PublicDirectoryApiSort | undefined {
  return sort === 'favoriteDesc' ? 'favoriteCountDesc' : undefined;
}

export {
  classifyDirectoryDistanceBucket,
  formatDistanceMeters,
  groupDirectoryShopsByDistance,
  haversineMeters,
  shopDistanceMeters,
  DIRECTORY_AREA_MAX_METERS,
  DIRECTORY_NEARBY_MAX_METERS,
  type DirectoryDistanceBucketId,
  type DirectoryDistanceSection,
  type FormatDistanceMetersLabels,
} from './directoryDistance.js';

export function normalizeDirectoryQuery(query: string): string {
  return query.trim().toLocaleLowerCase();
}

export function matchesDirectoryQuery(
  fields: ReadonlyArray<string | null | undefined>,
  query: string,
): boolean {
  const normalized = normalizeDirectoryQuery(query);
  if (normalized.length === 0) {
    return true;
  }
  return fields.some((field) => {
    if (typeof field !== 'string') {
      return false;
    }
    return field.toLocaleLowerCase().includes(normalized);
  });
}

function compareLocale(a: string, b: string): number {
  return a.localeCompare(b, undefined, { sensitivity: 'base', numeric: true });
}

export interface DirectoryOrgFacet {
  readonly code: string;
  readonly name: string;
}

export function buildOrgFacets(
  items: ReadonlyArray<{ readonly tenantCode: string; readonly tenantName: string }>,
): DirectoryOrgFacet[] {
  const byCode = new Map<string, DirectoryOrgFacet>();
  for (const item of items) {
    const code = item.tenantCode.trim();
    if (code.length === 0 || byCode.has(code)) {
      continue;
    }
    const tenantName =
      typeof item.tenantName === 'string' ? item.tenantName.trim() : '';
    const name = tenantName.length > 0 ? tenantName : code;
    byCode.set(code, { code, name });
  }
  return [...byCode.values()].sort((left, right) => compareLocale(left.name, right.name));
}

export function filterDirectoryOrgs<T extends { readonly name: string; readonly code: string }>(
  items: readonly T[],
  options: { readonly query: string; readonly sort: DirectorySortId },
): T[] {
  const matched = items.filter((item) => matchesDirectoryQuery([item.name, item.code], options.query));
  const sorted = [...matched];
  sorted.sort((left, right) => {
    const cmp = compareLocale(left.name, right.name);
    return options.sort === 'nameDesc' ? -cmp : cmp;
  });
  return sorted;
}

export function filterDirectoryShops<
  T extends {
    readonly name: string;
    readonly code: string;
    readonly location: string | null;
    readonly tenantCode: string;
    readonly tenantName: string;
    readonly lat?: number | null;
    readonly lng?: number | null;
    /** ONLINE_ONLY stays listable for name/org/favorite; proximity buckets omit (A11c). */
    readonly customerPresence?: 'STOREFRONT' | 'ONLINE_ONLY' | string | null;
  },
>(
  items: readonly T[],
  options: {
    readonly query: string;
    readonly sort: DirectorySortId;
    readonly orgCode: string | null;
    readonly userLat?: number;
    readonly userLng?: number;
  },
): T[] {
  const orgCode =
    typeof options.orgCode === 'string' && options.orgCode.trim().length > 0
      ? options.orgCode.trim().toLocaleLowerCase()
      : null;

  const matched = items.filter((item) => {
    if (orgCode !== null && item.tenantCode.trim().toLocaleLowerCase() !== orgCode) {
      return false;
    }
    return matchesDirectoryQuery(
      [item.name, item.code, item.location, item.tenantName, item.tenantCode],
      options.query,
    );
  });

  // Server already ranks by favoriteCountDesc — filter only; preserve page order.
  // A11c — ONLINE_ONLY remains OK under favorite (and name/org below).
  if (options.sort === 'favoriteDesc') {
    return matched;
  }

  const userLat = options.userLat;
  const userLng = options.userLng;
  const distanceSort =
    options.sort === 'distanceAsc' &&
    typeof userLat === 'number' &&
    typeof userLng === 'number' &&
    Number.isFinite(userLat) &&
    Number.isFinite(userLng);

  if (distanceSort) {
    // A11c — proximity/geo: ONLINE_ONLY lands in noCoordinates only (not nearby/area/further).
    return groupDirectoryShopsByDistance(matched, userLat, userLng).flatMap(
      (section) => section.items,
    );
  }

  // nameAsc / nameDesc / orgAsc — ONLINE_ONLY stays listable (A11c).
  const sorted = [...matched];
  sorted.sort((left, right) => {
    if (options.sort === 'orgAsc') {
      const orgCmp = compareLocale(left.tenantName, right.tenantName);
      if (orgCmp !== 0) {
        return orgCmp;
      }
      return compareLocale(left.name, right.name);
    }
    const cmp = compareLocale(left.name, right.name);
    return options.sort === 'nameDesc' ? -cmp : cmp;
  });
  return sorted;
}
